// Pure helpers for the CI_05 breadcrumb screens. No React imports — unit-testable.
import { canonicalRollup } from './BoardPage';

export const SCREEN_DEFS = [
  {
    id: 'exec',
    ord: '01',
    label: 'Executive summary',
    tip: 'Total variance, per-rollup subtotals, and the significant-items narrative'
  },
  {
    id: 'bs',
    ord: '02',
    label: 'Balance sheet',
    tip: 'Assets, liabilities, and equity movements vs the comparison basis'
  },
  {
    id: 'nim',
    ord: '03',
    label: 'NIM / rate-volume',
    tip: 'Interest income and expense with the rate vs volume decomposition'
  },
  {
    id: 'nir',
    ord: '04',
    label: 'Non-interest revenue',
    tip: 'Fee income lines with waterfalls and narratives'
  },
  {
    id: 'nie',
    ord: '05',
    label: 'Non-interest expense',
    tip: 'Expense lines with waterfalls and narratives'
  },
  {
    id: 'provision',
    ord: '06',
    label: 'Provision & allowance',
    tip: 'Credit provision movements vs the comparison basis'
  }
];

export const screenById = (id) =>
  SCREEN_DEFS.find((screen) => screen.id === id) || SCREEN_DEFS[0];

const SCREEN_ROLLUPS = {
  bs: ['Balance Sheet'],
  nim: ['Interest Income', 'Interest Expense'],
  nir: ['Non-Interest Income'],
  nie: ['Non-Interest Expense'],
  provision: ['Provision', 'Tax']
};

export const linesForScreen = (screenId, lines = []) => {
  const rollups = SCREEN_ROLLUPS[screenId];
  if (!rollups) return lines;
  return lines.filter((line) => rollups.includes(canonicalRollup(line.rollup)));
};

const BS_CLASS_ORDER = ['Assets', 'Liabilities', 'Equity'];

// Balance sheet groups by bsClass (Assets / Liabilities / Equity) — never GL-name regex.
export const groupByBsClass = (lines = []) => {
  const grouped = new Map();
  lines.forEach((line) => {
    const raw = String(line.bsClass || '').trim();
    const known = BS_CLASS_ORDER.find((name) => name.toLowerCase() === raw.toLowerCase());
    const label = known || 'Other balances';
    if (!grouped.has(label)) grouped.set(label, []);
    grouped.get(label).push(line);
  });
  return Array.from(grouped.entries())
    .map(([rollup, rows]) => ({
      rollup,
      rows: [...rows].sort((a, b) => String(a.glGroup || '').localeCompare(String(b.glGroup || '')))
    }))
    .sort((a, b) => {
      const ai = BS_CLASS_ORDER.indexOf(a.rollup);
      const bi = BS_CLASS_ORDER.indexOf(b.rollup);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
};

const CITATION_GROUPS = [
  ['bridge', ['bridgeComponentIds', 'bridge_component_ids', 'bridgeIds', 'bridge_ids']],
  ['finding', ['findingIds', 'finding_ids']],
  ['disposition', ['dispositionIds', 'disposition_ids']],
  ['comment', ['commentIds', 'comment_ids']],
  ['expectation', ['expectationIds', 'expectation_ids']],
  ['forecastContext', ['forecastContextIds', 'forecast_context_ids']],
  ['forecastOverride', ['forecastOverrideIds', 'forecast_override_ids']]
];

export const collectCitations = (citations = {}) =>
  CITATION_GROUPS.map(([type, keys]) => {
    const ids = keys.map((key) => citations?.[key]).find((value) => Array.isArray(value)) || [];
    return { type, ids };
  }).filter((group) => group.ids.length > 0);

const CITATION_LABELS = {
  forecastContext: ['forecast basis', 'forecast bases'],
  forecastOverride: ['forecast override', 'forecast overrides']
};

const plural = (n, singular, pluralWord) => {
  const labels = CITATION_LABELS[singular];
  if (labels) return `${n} ${n === 1 ? labels[0] : labels[1]}`;
  return `${n} ${n === 1 ? singular : pluralWord || `${singular}s`}`;
};

export const evidenceSummaryLine = (citations = {}) => {
  const groups = collectCitations(citations);
  if (!groups.length) return '';
  return groups
    .map(({ type, ids }) => plural(ids.length, type))
    .join(' · ');
};

const verdictOf = (finding = {}) =>
  String(finding?.disposition?.verdict || '').toLowerCase();

// Signal vs noise: the disposition feedback loop, structured.
export const bucketFindings = (findings = []) => {
  const buckets = { signal: [], knownRecurring: [], noise: [] };
  findings.forEach((finding) => {
    const status = String(finding.status || 'open').toLowerCase();
    const verdict = verdictOf(finding);
    if (verdict === 'known_recurring') buckets.knownRecurring.push(finding);
    else if (verdict === 'noise' || status === 'suppressed' || status === 'superseded') buckets.noise.push(finding);
    else buckets.signal.push(finding);
  });
  return buckets;
};

export const signalNoiseCounts = (buckets) =>
  `${buckets.signal.length} signal · ${buckets.knownRecurring.length} known recurring · ${buckets.noise.length} suppressed noise`;

export const findingsForScreen = (screenId, findings = [], lines = []) => {
  const glGroups = new Set(
    linesForScreen(screenId, lines).map((line) => String(line.glGroup || ''))
  );
  return findings.filter((finding) => glGroups.has(String(finding.glGroup || '')));
};

export const formatMillions = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 'n/a';
  const millions = n / 1e6;
  const absMillions = Math.abs(millions);
  // Guard-compatible $K materiality band (mirrors backend close_intel/llm.py::fmt_mm):
  // a material $25K–$49,999 value renders '$30K' / '-$30K' instead of being floored to
  // '$0.0MM'. Below $25K still floors, with negative-zero suppressed; $50K+ keeps $x.xMM.
  if (absMillions >= 0.025 && absMillions < 0.05) {
    return `${n < 0 ? '-' : ''}$${Math.abs(n / 1e3).toFixed(0)}K`;
  }
  if (absMillions < 0.05) return '$0.0MM';
  return `${millions < 0 ? '-' : ''}$${absMillions.toFixed(1)}MM`;
};

export const BASIS_LABELS = {
  budget: 'Budget / plan',
  prior: 'Latest forecast',
  prior_period: 'Prior period',
  prior_year: 'Prior year'
};

export const basisTabLabel = (basis) =>
  BASIS_LABELS[basis] || String(basis || '').replace(/_/g, ' ');

export const screenForRollup = (rollup) => {
  const canonical = canonicalRollup(rollup);
  if (canonical === 'Balance Sheet') return 'bs';
  if (canonical === 'Interest Income' || canonical === 'Interest Expense') return 'nim';
  if (canonical === 'Non-Interest Income') return 'nir';
  if (canonical === 'Non-Interest Expense') return 'nie';
  if (canonical === 'Provision' || canonical === 'Tax') return 'provision';
  return null;
};

export const screenForGlGroup = (glGroup, lines = []) => {
  const match = lines.find((line) => String(line.glGroup || '') === String(glGroup || ''));
  if (match) return screenForRollup(match.rollup);
  return null;
};

export const EXEC_WATERFALL_SCREENS = {
  nii: 'nim',
  nir: 'nir',
  nie: 'nie',
  provision: 'provision',
  tax: 'provision'
};
